import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { api } from "../../../../singleton/axios";
import type { EarningsReportResponse } from "../feed/types";

/** Notes still arrive after a report: the page reads again each minute while it is visible. */
export const REPORT_REFETCH_MS = 60 * 1000;

export const earningsReportKey = (eventId: string) =>
  ["earnings-report", eventId] as const;

const status = (error: unknown): number | undefined =>
  (error as AxiosError | null)?.response?.status;

/** One report's page: its company, results, Since report, every note and the reports before it. */
export const useEarningsReport = (eventId: string | undefined) =>
  useQuery<EarningsReportResponse, AxiosError>({
    queryKey: earningsReportKey(eventId ?? ""),
    queryFn: async ({ signal }) => {
      const { data } = await api.get<EarningsReportResponse>(
        `/earnings-report/${encodeURIComponent(eventId ?? "")}`,
        { signal },
      );
      return data;
    },
    enabled: eventId !== undefined && eventId !== "",
    staleTime: REPORT_REFETCH_MS,
    refetchInterval: REPORT_REFETCH_MS,
    refetchIntervalInBackground: false,
    // A 4xx (an unknown or expired report) is an answer, so it is never retried.
    retry: (failureCount, error) => {
      const code = status(error);
      if (code !== undefined && code >= 400 && code < 500) return false;
      return failureCount < 3;
    },
  });

/** The report is gone or never was. */
export const isReportNotFound = (error: unknown): boolean =>
  status(error) === 404;
